import {
    Box,
    Checkbox,
    List,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StructureSetRoiReadModel } from './types';
import { dicomColorToRgbString } from '~/utils/color';

type StructureListPanelProps = {
    filteredRois: StructureSetRoiReadModel[];
    selectedRois: Set<number>;
    disabledRoiNumbers: Set<number>;
    conflictingRoiNumbers: Set<number>;
    overwriteExisting: boolean;
    onToggleRoi: (roiNumber: number) => void;
};

export default function StructureListPanel({
    filteredRois,
    selectedRois,
    disabledRoiNumbers,
    conflictingRoiNumbers,
    overwriteExisting,
    onToggleRoi,
}: StructureListPanelProps) {
    const intl = useIntl();
    const theme = useTheme();

    return (
        <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
            {filteredRois.length === 0 ? (
                <Box px={2} py={3} textAlign="center">
                    <Typography fontSize={12} color="text.secondary">
                        <FormattedMessage
                            id="structureSet.copy.noMatchingRois"
                            defaultMessage="No structures match the current filters."
                        />
                    </Typography>
                </Box>
            ) : (
                filteredRois.map((roi) => {
                    const isDisabled = disabledRoiNumbers.has(roi.roiNumber);
                    const hasConflict = conflictingRoiNumbers.has(
                        roi.roiNumber
                    );
                    const checked =
                        !isDisabled && selectedRois.has(roi.roiNumber);

                    return (
                        <Box
                            key={roi.roiNumber}
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            px={1}
                            py={0.25}
                            sx={{
                                cursor: isDisabled ? 'default' : 'pointer',
                                '&:hover': isDisabled
                                    ? undefined
                                    : { bgcolor: 'action.hover' },
                                opacity: isDisabled ? 0.55 : 1,
                            }}
                            onClick={() =>
                                !isDisabled && onToggleRoi(roi.roiNumber)
                            }
                            data-testid={`roi-preview-${roi.roiNumber}`}
                        >
                            <Checkbox
                                size="small"
                                checked={checked}
                                disabled={isDisabled}
                                tabIndex={-1}
                                disableRipple
                                sx={{ p: 0.25 }}
                            />
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    bgcolor: dicomColorToRgbString(roi.color),
                                    opacity: isDisabled ? 0.5 : 1,
                                }}
                            />
                            <Typography fontSize={12} noWrap sx={{ flex: 1 }}>
                                {roi.name}
                            </Typography>
                            {hasConflict && (
                                <Tooltip
                                    placement="right"
                                    title={intl.formatMessage({
                                        id: 'structureSet.copy.nameConflict',
                                        defaultMessage: overwriteExisting
                                            ? 'This structure will overwrite the existing one in the target.'
                                            : 'A structure with this name already exists in the target.',
                                    })}
                                >
                                    <Box display="flex" flexShrink={0}>
                                        <AlertTriangle
                                            size={12}
                                            color={theme.palette.warning.main}
                                        />
                                    </Box>
                                </Tooltip>
                            )}
                            <Typography
                                fontSize={11}
                                color="text.secondary"
                                noWrap
                            >
                                {roi.interpretedType || '—'}
                            </Typography>
                        </Box>
                    );
                })
            )}
        </List>
    );
}
